// Types for carbon calculation
export interface CarbonSegment {
  label: string;
  value: number;
  tooltip?: {
    lines: Array<{
      label: string;
      value: string;
    }>;
  };
}

export interface ProcessChain {
  name: string;
  segments: CarbonSegment[];
  total?: number;
}

export interface LegendItem {
  label: string;
  color: string;
  value?: number; // Add value property for single chain legends
}

export interface CarbonFootprintData {
  materialEnergyConsumption: number;
  partEnergyConsumption: number;
  totalCo2: number;
  segments: CarbonSegment[];
  levels: Record<string, string>;
}

// Constants
export const SEGMENT_ORDER = ['material', 'pre process', 'main process', 'post process'];

export function calculateTotal(chain: ProcessChain): number {
  return chain.segments.reduce((sum, segment) => sum + (segment.value || 0), 0);
}

export function getMaxTotal(processChains: ProcessChain[]): number {
  return Math.max(...processChains.map(chain => chain.total || calculateTotal(chain)));
}

export function calculateTotalCO2(processChain: any, level: string): number {
  const total = processChain?.co2['co2' + level] || 0;
  return total;
}

export function getAllSegmentTypes(processChains: ProcessChain[]): string[] {
  const types = new Set<string>();

  processChains.forEach(chain => {
    chain.segments.forEach(segment => {
      types.add(segment.label.toLowerCase());
    });
  });

  return Array.from(types).sort((a, b) => {
    const aIndex = SEGMENT_ORDER.findIndex(order => a.includes(order));
    const bIndex = SEGMENT_ORDER.findIndex(order => b.includes(order));

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return 0;
  });
}

export function sortSegments(segments: CarbonSegment[]): CarbonSegment[] {
  return [...segments].sort((a, b) => {
    const aIndex = SEGMENT_ORDER.findIndex(order => a.label.toLowerCase().includes(order));
    const bIndex = SEGMENT_ORDER.findIndex(order => b.label.toLowerCase().includes(order));

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return 0;
  });
}

export function formatCO2Unit(unit: string): string {
  return unit.replace(/CO2|Co2|co2/gi, 'CO<sub>2</sub>');
}

export function calculateMaterialEnergyConsumption(processChain: any, level: string): number {
  return processChain?.co2['co2_ec' + level] || 0;
}

export function calculatePartEnergyConsumption(processChain: any, level: string): number {
  const total = processChain?.co2['co2' + level] || 0;
  const material = processChain?.co2['co2_ec' + level] || 0;
  return total - material;
}

export function createPartSegments(processChain: any, level: string): CarbonSegment[] {
  const segments: CarbonSegment[] = [];

  if (processChain && processChain.process_steps) {
    Object.keys(processChain.process_steps).forEach((step, index) => {
      const processStep = processChain.process_steps[step];
      const totalValue = processStep?.co2['co2' + level] || 0;
      const materialValue = processStep?.co2['co2_ec' + level] || 0;
      const value = Number((totalValue - materialValue).toFixed(2));

      if (value > 0) {
        segments.push({
          label: processStep.name || 'Process Step',
          value: value,
        });
      }
    });
  }

  return segments;
}

export function createMaterialSegment(materialEnergyConsumption: number): CarbonSegment {
  return {
    label: 'Material',
    value: Number(materialEnergyConsumption.toFixed(2)),
  };
}

export function combineCarbonSegments(
  materialEnergyConsumption: number,
  partSegments: CarbonSegment[]
): CarbonSegment[] {
  const segments = [...partSegments];
  if (materialEnergyConsumption >= 0.01) {
    const materialSegment = createMaterialSegment(materialEnergyConsumption);
    segments.unshift(materialSegment);
  }

  return segments;
}

export function differenceMainBechmark(
  mainProcessChain: any,
  benchmarkProcessChain: any,
  selectedLevel: Record<string, string>
): string {
  const level = Object.keys(selectedLevel)[0];
  const mainProcessChainTotal = calculateTotalCO2(mainProcessChain, level);
  const benchmarkProcessChainTotal = calculateTotalCO2(benchmarkProcessChain, level);
  return (benchmarkProcessChainTotal - mainProcessChainTotal).toFixed(2);
}

export function generateLevelLabels(processChain: any): Record<string, string> {
  const labels = {
    _pp: 'Part',
    _pj: '',
    _pl: 'Lot [' + processChain.lot_size + ' parts]',
  };

  if (processChain.lot_only) {
    labels['_pj'] = 'Job [Ø ' + processChain.parts_pj + ' parts]';
  } else {
    labels['_pj'] = 'Job [' + processChain.parts_pj + ' parts]';
  }

  return labels;
}

export function processCarbonFootprintData(
  processChain: any,
  selectedLevel: Record<string, string>
): CarbonFootprintData {
  const level = Object.keys(selectedLevel)[0];

  const materialEnergyConsumption = calculateMaterialEnergyConsumption(processChain, level);
  const partEnergyConsumption = calculatePartEnergyConsumption(processChain, level);
  const totalCo2 = calculateTotalCO2(processChain, level);

  const partSegments = createPartSegments(processChain, level);
  const segments = combineCarbonSegments(materialEnergyConsumption, partSegments);

  const levels = generateLevelLabels(processChain);

  return {
    materialEnergyConsumption,
    partEnergyConsumption,
    totalCo2,
    segments,
    levels,
  };
}
